import styled from "@emotion/styled";
import { flexRender, Row } from "@tanstack/react-table";
import { ActivityDetailedView } from "components/Activity/ActivityDetailedView/ActivityDetailedView";
import { Spacer } from "components/general/Spacer/Spacer";
import { useIsMobile } from "hooks/useDeviceType";
import { useIsTradeHistoryReturned } from "hooks/useIsTradeHistoryReturned";
import { FC, useMemo, useState } from "react";
import { ActivityStore } from "store/activity/ActivityStore";
import { TransactionDetail } from "types/history";
import { withIsVisible } from "utils/reactUtils";
import { MonthRowContent } from "./MonthRowContent";
import { TableRowMobile } from "./TableRowMobile";

interface TableRowInterface {
  row: Row<TransactionDetail>;
  firstTxOfMonth: boolean;
  lastTxOfMonth: boolean;
  rowIndex: number;
}

interface TableRowDesktopProps {
  row: Row<TransactionDetail>;
  onClick: (row: Row<TransactionDetail>) => void;
}

const TableRowDesktop: FC<TableRowDesktopProps> = ({ row, onClick }) => {
  return (
    <>
      {row.getVisibleCells().map((cell, index) => {
        const cellSize = cell.column.getSize();
        if (cellSize === 0) return null;
        return (
          <Container onClick={() => onClick(row)} style={{ width: `${cellSize}%` }} key={index}>
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </Container>
        );
      })}
    </>
  );
};

interface DetailsRowProps {
  row: Row<TransactionDetail>;
  showBorderTop: boolean;
  lastTxOfMonth: boolean;
  showHoverState: boolean;
}

const DetailsRow: FC<DetailsRowProps> = ({ row, showBorderTop, lastTxOfMonth, showHoverState }) => {
  const isMobile = useIsMobile();
  const history = ActivityStore.useState(s => s.history);
  const txHash = useMemo(() => row.getValue<TransactionDetail["txHash"]>("txHash"), [row]);
  const [isDetailedViewOpen, setIsDetailedViewOpen] = useState(false);
  const openDetailedView = () => setIsDetailedViewOpen(true);
  const closeDetailedView = () => setIsDetailedViewOpen(false);

  return (
    <>
      {isMobile ? (
        <WrapperMobile isVisible={isMobile} onClick={openDetailedView}>
          {/* Since mobile does not render a table-like structure, it does not use
        .getVisibleCells to loop through pre-formatted cells in useActivityColumnDefinitions.tsx */}
          <TableRowMobile rowDetails={row} />
        </WrapperMobile>
      ) : (
        <Wrapper
          isVisible={!isMobile}
          showBorderTop={showBorderTop}
          lastTxOfMonth={lastTxOfMonth}
          showHoverState={showHoverState}
        >
          <TableRowDesktop row={row} onClick={openDetailedView} />
        </Wrapper>
      )}
      {history.data?.results[txHash] != null && (
        <ActivityDetailedView
          open={isDetailedViewOpen}
          closeView={closeDetailedView}
          onClose={closeDetailedView}
          placement="right"
          width={`max(${isMobile ? 100 : 33}%, 400px)`}
          txHash={txHash}
        />
      )}
    </>
  );
};

interface MonthRowProps {
  isMobile: boolean;
  row: Row<TransactionDetail>;
  rowIndex: number;
}

const MonthRow = withIsVisible(({ isMobile, row, rowIndex }: MonthRowProps) => {
  return (
    <tr>
      <td>
        {rowIndex !== 0 && <Spacer height={isMobile ? "10px" : "12px"} />}
        <MonthRowContent date={row.original.timestamp} />
      </td>
    </tr>
  );
});

export const TableRow = ({ row, firstTxOfMonth, lastTxOfMonth, rowIndex }: TableRowInterface) => {
  const isMobile = useIsMobile();
  const isTradeHistoryReturned = useIsTradeHistoryReturned();

  if (row == null) {
    return null;
  }

  return (
    <>
      <MonthRow isVisible={firstTxOfMonth} row={row} rowIndex={rowIndex} isMobile={isMobile} />
      <DetailsRow
        row={row}
        showBorderTop={!firstTxOfMonth}
        lastTxOfMonth={lastTxOfMonth}
        showHoverState={isTradeHistoryReturned}
      />
    </>
  );
};

// Sub-components

const Wrapper = withIsVisible(styled.tr<{
  showBorderTop: boolean;
  lastTxOfMonth: boolean;
  showHoverState: boolean;
}>`
  max-height: 100%;
  overflow: auto;
  border-top: ${props =>
      props.showBorderTop ? props.theme.surface.main.secondary.default : "transparent"}
    1px solid;
  border-radius: 17px;
  cursor: ${props => (props.showHoverState ? "pointer" : "inherit")};

  &:hover {
    background: ${props => props.theme.surface.main.secondary.default};
    border-top: transparent 1px solid;
    transition: background 250ms ease-in-out;
  }

  &:hover + tr {
    // "none" takes up 0px height, while "transparent 1px solid" gives an invisible 1px height
    border-top: ${props => (props.lastTxOfMonth ? "none" : "transparent 1px solid")};
  }

  td:first-of-type {
    border-top-left-radius: 17px;
    border-bottom-left-radius: 17px;
  }

  td:last-of-type {
    border-top-right-radius: 17px;
    border-bottom-right-radius: 17px;
  }
`);

const WrapperMobile = withIsVisible(styled.tr`
  max-height: 100%;
  overflow: auto;
`);

const Container = styled.td`
  padding: 12px;
  margin: auto;
  align-items: center;
  gap: 12px;
  align-self: stretch;
  justify-content: space-between;
  max-height: 60px;
`;
